"use client";

import { useTranslations } from "next-intl";
import type * as React from "react";
import type { GlobalStakeStats } from "@/hooks/stake/useGlobalStakeStats";
import { formatStakeNumber } from "@/lib/stake/format-number";
import { cn } from "@/lib/utils";

interface UvmTotalsPanelProps {
  stats: GlobalStakeStats | undefined;
}

const Row: React.FC<{ label: string; value: string; last?: boolean }> = ({
  label,
  value,
  last,
}) => (
  <div
    className={cn(
      // Mobile: each on its own full-width line — label follows RTL (right),
      // number follows its dir=ltr (left); no forced text alignment.
      // ≥sm: label and number share one line.
      "flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0",
      !last && "border-b border-stroke-light",
    )}
  >
    <span className="text-xs text-text-secondary">{label}</span>
    <span className="text-sm font-bold text-text-primary font-inter" dir="ltr">
      {value}
    </span>
  </div>
);

/** Dark panel: total UVM supply / staked / cumulative rewarded. */
export const UvmTotalsPanel: React.FC<UvmTotalsPanelProps> = ({ stats }) => {
  const t = useTranslations("stake");
  return (
    <div className="rounded-lg bg-bg-secondary border border-stroke-light p-4">
      <h3 className="text-sm font-semibold text-text-primary text-center mb-2">
        {t("globalStats.uvmReportTitle")}
      </h3>
      <Row
        label={t("globalStats.totalCount")}
        value={stats ? formatStakeNumber(stats.totalUvmSupplyWei) : "—"}
      />
      <Row
        label={t("globalStats.staked")}
        value={stats ? formatStakeNumber(stats.totalUvmStakedWei) : "—"}
      />
      <Row
        label={t("globalStats.rewarded")}
        value={stats ? formatStakeNumber(stats.totalUvmRewardedWei) : "—"}
        last
      />
    </div>
  );
};
